/**
 * Shared config for the Dynamic Reports module (Manage + Run).
 *
 * The backend describes each saved report as a base report_name plus a list
 * of filter_name strings ("Property", "Start Date", "Year", …). This module
 * turns those strings into V3 ReportFilterBar field configs, maps a report
 * to its /reports endpoint, and supplies the (money/count-aware) column
 * catalogue per base report — so both the editor and the runner share one
 * source of truth instead of the duplicated switch statements they had.
 */
import { formatCount } from './ReportTable';

export const TODAY = new Date().toISOString().split('T')[0];

const MONTH_KEYS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

export const monthOptions = (t) =>
    MONTH_KEYS.map((m, i) => ({ value: String(i + 1), label: t(m) }));

export const yearOptions = () => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2].map((v) => ({ value: String(v), label: String(v) }));
};

/**
 * Map one backend filter to a ReportFilterBar field.
 *   filterName — the backend label ("Property", "Start Date", …)
 *   key        — the values-object key this field reads/writes
 *   lookups    — useReportLookups() result (option arrays)
 */
export const buildDynamicField = (filterName, key, t, lookups) => {
    const base = { key, label: filterName };
    switch (filterName) {
        case 'Year':
            return { ...base, type: 'select', options: yearOptions(), placeholder: 'Select Year' };
        case 'Start Date':
            return { ...base, type: 'date', role: 'startDate', max: TODAY, placeholder: 'Start date' };
        case 'End Date':
            return { ...base, type: 'date', role: 'endDate', max: TODAY, placeholder: 'End date' };
        case 'Start Month':
            return { ...base, type: 'select', options: monthOptions(t), placeholder: 'Select Start Month' };
        case 'End Month':
            return { ...base, type: 'select', options: monthOptions(t), placeholder: 'Select End Month' };
        case 'Property':
            return { ...base, type: 'select', options: lookups.propertyOptions, placeholder: 'All properties' };
        case 'Category':
            return { ...base, type: 'select', options: lookups.categoryOptions, placeholder: 'All categories' };
        case 'Subcategory':
            return { ...base, type: 'select', options: lookups.subcategoryOptions, placeholder: 'All sub categories' };
        case 'Group':
            return { ...base, type: 'select', options: lookups.groupOptions, placeholder: 'All groups' };
        case 'Tenant':
            return { ...base, type: 'select', options: lookups.tenantOptions, placeholder: 'All tenants' };
        case 'Floor':
            return { ...base, type: 'select', options: lookups.floorOptions, placeholder: 'All floors' };
        default:
            return { ...base, type: 'text', placeholder: filterName };
    }
};

/** report_name → /reports endpoint slug. */
export const REPORT_ENDPOINTS = {
    'Daywise Report': 'getdaywisereport',
    'Sales Summary Report': 'getdaywiseconsolidatedreport',
    'Revenue Share Report': 'getrevenuesharereport',
    'Trading Density Report': 'gettradingdensityreport',
    'F&B Sales Summary Report': 'getfbsalessummary',
    'Sales Report-Delivery Partner': 'getdeliverypartnersales',
};

// Keys whose values are monetary / countable — drives right-align + number
// formatting + Excel cell types. Matched case-insensitively as a fallback to
// the explicit per-key sets below.
const MONEY_KEYS = new Set([
    'net_sales', 'total_net_sales', 'dine_in_sales', 'delivery_partner_sales',
    'total_delivery_partner_sales', 'revenue_share_amount', 'tenant_base_rent',
    'minimum_rent_per_sqft', 'rent_per_sqft', 'atv', 'trading_density',
]);
const COUNT_KEYS = new Set([
    'transaction_count', 'total_transaction_count', 'dine_in_count',
    'delivery_partner_count', 'total_delivery_partner_count', 'live_days',
]);

const classify = (key) => {
    const k = key.toLowerCase();
    const isMoney = MONEY_KEYS.has(k) || k.endsWith('_sales');
    const isCount = !isMoney && (COUNT_KEYS.has(k) || k.endsWith('_count'));
    return {
        isMoney,
        isCount,
        align: isMoney || isCount ? 'right' : undefined,
        format: isCount ? formatCount : undefined,
    };
};

const BASE_COLUMNS = {
    'Daywise Report': [
        ['tenant_store_name', 'Tenant Name'], ['tenant_group_name', 'Group Name'],
        ['tenant_code', 'Tenant Code'], ['tenant_unit_code', 'Unit Code'],
        ['property_name', 'Property'], ['subcategory_name', 'Subcategory'],
        ['floor', 'Floor'], ['transaction_date', 'Date'],
        ['transaction_count', 'Transaction Count'], ['net_sales', 'Net Sales'],
    ],
    'Sales Summary Report': [
        ['tenant_store_name', 'Tenant Name'], ['tenant_group_name', 'Group Name'],
        ['tenant_code', 'Tenant Code'], ['tenant_unit_code', 'Unit Code'],
        ['property_name', 'Property'], ['subcategory_name', 'Subcategory'],
        ['floor', 'Floor'], ['total_transaction_count', 'Transaction Count'],
        ['total_net_sales', 'Net Sales'],
    ],
    'Revenue Share Report': [
        ['tenant_store_name', 'Tenant Name'], ['tenant_group_name', 'Group Name'],
        ['tenant_code', 'Tenant Code'], ['tenant_unit_code', 'Unit Code'],
        ['property_name', 'Property'], ['subcategory_name', 'Subcategory'],
        ['floor', 'Floor'], ['total_transaction_count', 'Transaction Count'],
        ['total_net_sales', 'Net Sales'], ['tenant_base_rent', 'Base Rent'],
        ['tenant_revenue_share_percentage', 'Revenue Share %'],
        ['revenue_share_amount', 'Revenue Share Amount'],
        ['tenant_chargable_area', 'Store Area'],
        ['minimum_rent_per_sqft', 'Minimum Rent per SQF'], ['rent_per_sqft', 'Rent per SQF'],
        ['lease_start_date', 'Lease Start Date'], ['lease_end_date', 'Lease End Date'],
    ],
    'Trading Density Report': [
        ['tenant_store_name', 'Tenant Name'], ['tenant_group_name', 'Group Name'],
        ['tenant_code', 'Tenant Code'], ['tenant_unit_code', 'Unit Code'],
        ['property_name', 'Property'], ['subcategory_name', 'Subcategory'],
        ['floor', 'Floor'], ['tenant_chargable_area', 'Area'],
        ['total_transaction_count', 'Transaction Count'], ['total_net_sales', 'Net Sales'],
        ['trading_density', 'Trading Density'], ['atv', 'ATV'],
    ],
    'F&B Sales Summary Report': [
        ['tenant_store_name', 'Tenant Name'], ['tenant_group_name', 'Group Name'],
        ['tenant_code', 'Tenant Code'], ['tenant_unit_code', 'Unit Code'],
        ['property_name', 'Property'], ['subcategory_name', 'Subcategory'],
        ['dine_in_count', 'Dine In Count'], ['dine_in_sales', 'Dine In Sales'],
    ],
    'Sales Report-Delivery Partner': [
        ['delivery_partner_name', 'Delivery Partner Name'],
        ['delivery_partner_count', 'Count'], ['delivery_partner_sales', 'Net Sales'],
    ],
};

/**
 * Column catalogue for a base report, enriched with money/count formatting.
 * For F&B Sales Summary, appends the per-delivery-partner columns (and the
 * row data must be flattened — see flattenPartnerRows).
 */
export const getReportColumns = (reportName, t, deliveryPartners = []) => {
    const base = (BASE_COLUMNS[reportName] || []).map(([key, label]) => ({ key, label, ...classify(key) }));
    if (reportName === 'F&B Sales Summary Report' && deliveryPartners.length) {
        deliveryPartners.forEach((p) => {
            const k = p.toLowerCase().replace(/ /g, '_');
            base.push(
                { key: `${k}_count`, label: t('{{partner}} Count', { partner: p }), align: 'right', isCount: true, format: formatCount },
                { key: `${k}_sales`, label: t('{{partner}} Sales', { partner: p }), isMoney: true, align: 'right' },
            );
        });
    }
    return base;
};

/** Flatten nested delivery_partner_counts/_sales onto each F&B row. */
export const flattenPartnerRows = (rows, deliveryPartners = []) =>
    rows.map((item) => {
        const row = { ...item };
        deliveryPartners.forEach((p) => {
            const k = p.toLowerCase().replace(/ /g, '_');
            row[`${k}_count`] = item.delivery_partner_counts?.[`${k}_count`] ?? 0;
            row[`${k}_sales`] = Number(item.delivery_partner_sales?.[`${k}_sales`] ?? 0);
        });
        return row;
    });

/** ReportExportButtons column descriptors from a table column catalogue. */
export const toExportColumns = (columns) =>
    columns.map((c) => ({
        key: c.key,
        header: c.label,
        isMoney: c.isMoney === true,
        isCount: c.isCount === true,
        total: false,
    }));
